// src/components/character/ARCreateSection.tsx
// AR linking section for CharacterEditModal - links to appeear.me

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Character } from '../../types/character';
import { arService, ARModelInfo } from '../../services/arService';
import { config } from '../../config';
import {
  CubeIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ARCreateSectionProps {
  character: Character | null;
  onARLinked: (arModelId: string) => void;
  onARUnlinked: () => void;
}

const ARCreateSection: React.FC<ARCreateSectionProps> = ({
  character,
  onARLinked,
  onARUnlinked
}) => {
  const { t } = useTranslation('character');
  const [myModels, setMyModels] = useState<ARModelInfo[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if character already has AR model
  const hasARModel = character?.arModelId && character.arModelStatus === 'active';

  // Fetch user's AR models from appeear.me
  const fetchMyModels = async () => {
    setIsLoadingModels(true);
    setError(null);
    try {
      const models = await arService.getMyARModels();
      setMyModels(models);
      setShowModelSelector(true);
    } catch (err) {
      console.error('Failed to fetch AR models:', err);
      setError(t('edit.ar.errors.fetchFailed'));
    } finally {
      setIsLoadingModels(false);
    }
  };

  // Handle model selection
  const handleSelectModel = (modelId: string) => {
    onARLinked(modelId);
    setShowModelSelector(false);
  };

  // Handle unlink
  const handleUnlink = async () => {
    if (!character?.id) return;
    try {
      await arService.unlinkARModel(character.id);
      onARUnlinked();
    } catch (err) {
      console.error('Failed to unlink AR model:', err);
      setError(t('edit.ar.errors.unlinkFailed'));
    }
  };

  // Open appeear.me in new tab
  const openAppeearMe = () => {
    window.open(config.appeearMeUrl, '_blank');
  };

  if (!character) return null;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <CubeIcon className="w-4 h-4 text-white/60" />
        <h3 className="text-xs font-medium text-white/60 tracking-wide uppercase">
          {t('edit.ar.title')}
        </h3>
      </div>

      {/* Already has AR model */}
      {hasARModel && !showModelSelector && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="text-sm">{t('edit.ar.linked')}</span>
          </div>
          <a
            href={arService.getViewerUrl(character.arModelId!)}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-2 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors"
          >
            {t('edit.ar.viewInAR')}
          </a>
          <button
            onClick={fetchMyModels}
            disabled={isLoadingModels}
            className="w-full py-2 bg-white/10 hover:bg-white/20 text-white/70 text-xs rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            {isLoadingModels ? (
              <>
                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                {t('edit.ar.loading')}
              </>
            ) : (
              <>
                <ArrowPathIcon className="w-3.5 h-3.5" />
                {t('edit.ar.changeModel')}
              </>
            )}
          </button>
          <button
            onClick={handleUnlink}
            className="w-full py-2 bg-white/10 hover:bg-red-500/20 text-white/70 hover:text-red-400 text-xs rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            {t('edit.ar.unlink')}
          </button>
        </div>
      )}

      {/* No AR model - show link options */}
      {!hasARModel && !showModelSelector && (
        <div className="space-y-3">
          <p className="text-[10px] text-white/40">
            {t('edit.ar.description')}
          </p>

          {/* Open appeear.me button */}
          <button
            onClick={openAppeearMe}
            className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            {t('edit.ar.createInAppeearMe')}
          </button>

          {/* Link existing model button */}
          <button
            onClick={fetchMyModels}
            disabled={isLoadingModels}
            className="w-full py-2 bg-white/10 hover:bg-white/20 text-white/70 text-xs rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            {isLoadingModels ? (
              <>
                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                {t('edit.ar.loading')}
              </>
            ) : (
              t('edit.ar.linkExisting')
            )}
          </button>

          <p className="text-[9px] text-white/30 text-center">
            {t('edit.ar.loginHint')}
          </p>
        </div>
      )}

      {/* Model selector */}
      {showModelSelector && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/60">
              {hasARModel ? t('edit.ar.changeModelTitle') : t('edit.ar.selectModel')}
            </p>
            <button
              onClick={() => setShowModelSelector(false)}
              className="text-white/40 hover:text-white/60"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {myModels.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-white/40 mb-2">
                {t('edit.ar.noModels')}
              </p>
              <button
                onClick={openAppeearMe}
                className="text-xs text-purple-400 hover:text-purple-300"
              >
                {t('edit.ar.createLink')}
              </button>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {myModels.map((model) => (
                <button
                  key={model.modelId}
                  onClick={() => handleSelectModel(model.modelId)}
                  className="w-full flex items-center gap-3 p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-left"
                >
                  {model.thumbnailUrl ? (
                    <img
                      src={model.thumbnailUrl}
                      alt={model.name}
                      className="w-10 h-10 rounded object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                      <CubeIcon className="w-5 h-5 text-white/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{model.name}</p>
                    <p className="text-[10px] text-white/40">
                      {model.displayType === 'acrylic_stand' ? t('edit.ar.displayType.acrylicStand') : t('edit.ar.displayType.flat')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={fetchMyModels}
            disabled={isLoadingModels}
            className="w-full py-1.5 text-[10px] text-white/40 hover:text-white/60 flex items-center justify-center gap-1"
          >
            <ArrowPathIcon className={`w-3 h-3 ${isLoadingModels ? 'animate-spin' : ''}`} />
            {t('edit.ar.refreshList')}
          </button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mt-3 text-xs text-red-400 text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default ARCreateSection;
